import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { makeStyles } from '@mui/styles';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  ArrowDropDownRounded,
  HomeRounded
} from '@mui/icons-material';
import moment from 'moment/min/moment-with-locales';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from 'components/CustomButtons/Button.js';
import { FONT_FAMILY } from 'common/sharedFunctions.js';
import styles from '../../styles/headerLinksStyle.js';

const useStyles = makeStyles(styles);

export default function HeaderLinks({ mobile = false, onNavigate }) {
  const classes = useStyles();
  const settings = useSelector((state) => state.settingsdata.settings);
  const languagedata = useSelector((state) => state.languagedata);
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState(false);
  const [langSelection, setLangSelection] = useState();
  const [multiLanguage, setMultiLanguage] = useState();

  useEffect(() => {
    if (!languagedata.langlist) return;

    const selectedKey = Object.keys(languagedata.langlist).find(
      (key) => languagedata.langlist[key].langLocale === i18n.language
    );
    setLangSelection(selectedKey);
    setMultiLanguage(languagedata.langlist);
  }, [i18n.language, languagedata.langlist]);

  const handleNavigation = (path) => {
    navigate(path);
    if (onNavigate) onNavigate();
  };

  const handleLanguageSelect = (key) => {
    const language = multiLanguage[key];
    i18n.addResourceBundle(language.langLocale, 'translations', language.keyValuePairs);
    i18n.changeLanguage(language.langLocale);
    setLangSelection(key);
    moment.locale(language.dateLocale);
    setIsActive(false);
  };

  const navigationItems = [
    { icon: <HomeRounded />, label: t('home'), path: '/' }
  ];

  const socialItems = settings ? [
    {
      className: 'fab fa-facebook-f',
      href: settings.FacebookHandle,
      label: t('follow_facebook')
    },
    {
      className: 'fab fa-twitter',
      href: settings.TwitterHandle,
      label: t('follow_twitter')
    },
    {
      className: 'fab fa-instagram',
      href: settings.InstagramHandle,
      label: t('follow_instagram')
    }
  ].filter((item) => item.href) : [];

  const languageKeys = multiLanguage ? Object.keys(multiLanguage) : [];
  const selectedLanguage = multiLanguage && langSelection !== undefined
    ? multiLanguage[langSelection]
    : null;

  return (
    <List className={classes.list}>
      {navigationItems.map((item) => (
        <ListItem className={classes.listItem} key={item.path}>
          <Button
            color="transparent"
            className={classes.navLink}
            onClick={() => handleNavigation(item.path)}
          >
            <span className={classes.navIcon}>{item.icon}</span>
            <Typography className={classes.navLabel} fontFamily={FONT_FAMILY}>
              {item.label}
            </Typography>
          </Button>
        </ListItem>
      ))}

      {socialItems.length > 0 ? (
        <ListItem className={classNames(classes.listItem, classes.socialListItem)}>
          <div className={classes.socialGroup}>
            {socialItems.map((item) => (
              <Tooltip
                key={item.href}
                title={item.label}
                placement={mobile ? 'left' : 'top'}
                classes={{ tooltip: classes.tooltip }}
              >
                <Button
                  aria-label={item.label}
                  color="transparent"
                  className={classes.socialLink}
                  href={item.href}
                  rel="noreferrer"
                  target="_blank"
                >
                  <i className={classNames(classes.socialIcons, item.className)} />
                </Button>
              </Tooltip>
            ))}
          </div>
        </ListItem>
      ) : null}

      {languageKeys.length > 1 && selectedLanguage ? (
        <ListItem className={classNames(classes.listItem, classes.languageListItem)}>
          <div className={classes.languageControl}>
            <button
              aria-expanded={isActive}
              className={classes.toggleDropdown}
              onClick={() => setIsActive((active) => !active)}
              type="button"
            >
              <span>{selectedLanguage.langName}</span>
              <ArrowDropDownRounded className={isActive ? classes.dropdownIconActive : classes.dropdownIcon} />
            </button>

            <ul className={classNames(classes.optionUl, isActive && classes.optionUlActive)}>
              {languageKeys.map((key) => (
                <li
                  className={classes.option}
                  key={key}
                  onClick={() => handleLanguageSelect(key)}
                >
                  {multiLanguage[key].langName}
                </li>
              ))}
            </ul>
          </div>
        </ListItem>
      ) : null}
    </List>
  );
}
